import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { SafeStyle, SafeUrl } from '@angular/platform-browser';
import {
  CropperPosition,
  Dimensions,
  ImageCroppedEvent,
  ImageTransform,
  MoveStart,
  ExifTransform,
  MoveTypes,
} from '../interfaces/image-cropper.interfaces';
import { SafeResourceUrlService } from '../../../services/safe-resource-url.service';
import { ImageLoadingService } from '../services/image-loading.service';
import { ImageTransformService } from '../services/image-transform.service';
import { CropperPositionService } from '../services/cropper-position.service';
import { CropOperationsService } from '../services/crop-operations.service';
import { GestureService } from '../services/gesture.service';
import { getTransformationsFromExifData, supportsAutomaticRotation } from '../utils/image-cropper';

@Component({
  selector: 'image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrl: './image-cropper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ImageCropperComponent implements OnChanges, OnInit, OnDestroy {
  private originalImage: HTMLImageElement | null;
  private transformedImage: HTMLImageElement;
  private originalBase64: string;
  private moveStart: MoveStart;
  private originalSize: Dimensions;
  private transformedSize: Dimensions;
  private setImageMaxSizeRetries: number = 0;
  private imageSizeCheckInterval: number | null = null;
  private readonly MAX_RETRY_ATTEMPTS: number = 40;
  private readonly RETRY_INTERVAL_MS: number = 50;
  private cropperScaledMinWidth: number = 20;
  private cropperScaledMinHeight: number = 20;
  private exifTransform: ExifTransform = { rotate: 0, flip: false };
  private autoRotateSupported: Promise<boolean>;
  private stepSize: number = 3;

  safeImgDataUrl: SafeUrl | string;
  safeTransformStyle: SafeStyle | string;
  marginLeft: SafeStyle | string = '0px';
  maxSize: Dimensions;
  imageVisible: boolean = false;
  moveTypes: typeof MoveTypes = MoveTypes;

  @ViewChild('wrapper', { static: true }) wrapper: ElementRef;
  @ViewChild('sourceImage', { static: false }) sourceImage: ElementRef;

  @Input() imageChangedEvent: any;
  @Input() imageFile: File;

  @Input() format: 'png' | 'jpeg' | 'bmp' | 'webp' | 'tiff' | 'tif' | 'ico' = 'png';
  @Input() maintainAspectRatio: boolean = true;
  @Input() transform: ImageTransform = {};
  @Input() aspectRatio: number = 1;
  @Input() resizeToWidth: number = 0;
  @Input() cropperMinWidth: number = 0;
  @Input() canvasRotation: number = 0;
  @Input() initialStepSize: number = 3;
  @Input() roundCropper: boolean = false;
  @Input() onlyScaleDown: boolean = false;
  @Input() backgroundColor: string;
  @Input() containWithinAspectRatio: boolean = false;
  @Input() cropper: CropperPosition = {
    x1: -100,
    y1: -100,
    x2: 10000,
    y2: 10000,
  };
  @HostBinding('style.text-align')
  @Input()
  alignImage: 'left' | 'center' = 'center';

  @Output() imageCropped: EventEmitter<ImageCroppedEvent> = new EventEmitter<ImageCroppedEvent>();
  @Output() imageLoaded: EventEmitter<void> = new EventEmitter<void>();
  @Output() cropperReady: EventEmitter<Dimensions> = new EventEmitter<Dimensions>();
  @Output() loadImageFailed: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private readonly safeResourceUrlService: SafeResourceUrlService,
    private readonly cd: ChangeDetectorRef,
    private readonly imageLoadingService: ImageLoadingService,
    private readonly imageTransformService: ImageTransformService,
    private readonly cropperPositionService: CropperPositionService,
    private readonly cropOperationsService: CropOperationsService,
    private readonly gestureService: GestureService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.onChangesInputImage(changes);
    this.handleImageTransformChanges(changes);
    this.handleCropperChanges(changes);
    this.handleAspectRatioChanges(changes);
    this.handleTransformChanges(changes);
  }

  ngOnInit(): void {
    this.initCropper();
    this.autoRotateSupported = supportsAutomaticRotation();
    this.stepSize = this.initialStepSize;
    this.gestureService.activatePinchGesture(this.wrapper.nativeElement);
  }

  ngOnDestroy(): void {
    this.clearImageSizeCheck();
  }

  private handleImageTransformChanges(changes: SimpleChanges): void {
    if (
      this.originalImage &&
      this.originalImage.complete &&
      this.exifTransform &&
      (changes.containWithinAspectRatio || changes.canvasRotation)
    ) {
      this.transformOriginalImage();
    }
  }

  private handleCropperChanges(changes: SimpleChanges): void {
    if (changes.cropper) {
      this.setMaxSize();
      this.setCropperScaledMinSize();
      this.cropperPositionService.checkCropperPosition(this.cropper, this.maxSize, false);
      this.crop();
      this.cd.markForCheck();
    }
  }

  private handleAspectRatioChanges(changes: SimpleChanges): void {
    if (changes.aspectRatio && this.imageVisible) {
      this.resetCropperPosition();
    }
  }

  private handleTransformChanges(changes: SimpleChanges): void {
    if (changes.transform) {
      this.transform = this.transform || {};
      this.setCssTransform();
      this.crop();
    }
  }

  private onChangesInputImage(changes: SimpleChanges): void {
    if (changes.imageChangedEvent || changes.imageFile) {
      this.initCropper();
    }
    if (changes.imageChangedEvent && this.imageChangedEvent?.target?.files?.length > 0) {
      this.loadImageFile(this.imageChangedEvent.target.files[0]);
    }
    if (changes.imageFile && this.imageFile) {
      this.loadImageFile(this.imageFile);
    }
  }

  private setCssTransform(): void {
    this.safeTransformStyle = this.safeResourceUrlService.toSafeStyle(
      this.imageTransformService.createCssTransform(this.transform)
    );
  }

  private initCropper(): void {
    this.imageVisible = false;
    this.transformedImage = null;
    this.safeImgDataUrl =
      'data:image/png;base64,iVBORw0KGg' +
      'oAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAC0lEQVQYV2NgAAIAAAU' +
      'AAarVyFEAAAAASUVORK5CYII=';
    this.moveStart = this.gestureService.initMoveStart();
    this.maxSize = { width: 0, height: 0 };
    this.originalSize = { width: 0, height: 0 };
    this.transformedSize = { width: 0, height: 0 };
    this.cropper = this.cropperPositionService.initCropperPosition();
  }

  private async loadImageFile(file: File): Promise<void> {
    try {
      const imageBase64: string = await this.imageLoadingService.loadImageFile(file);
      await this.loadBase64Image(imageBase64);
    } catch {
      this.loadImageFailed.emit();
    }
  }

  private async loadBase64Image(imageBase64: string): Promise<void> {
    try {
      const supported: boolean = await this.autoRotateSupported;
      const processedBase64: string = await this.imageLoadingService.processImageBase64(imageBase64);
      this.originalImage = await this.imageLoadingService.createImageFromBase64(processedBase64);
      this.originalBase64 = processedBase64;
      this.exifTransform = getTransformationsFromExifData(supported ? -1 : processedBase64);
      this.originalSize.width = this.originalImage.naturalWidth;
      this.originalSize.height = this.originalImage.naturalHeight;
      await this.transformOriginalImage();
    } catch {
      this.loadImageFailed.emit();
      this.originalImage = null;
      this.originalBase64 = null;
    }
  }

  private async transformOriginalImage(): Promise<void> {
    if (!this.originalImage || !this.originalImage.complete || !this.exifTransform) {
      return Promise.reject(new Error('No image loaded'));
    }
    const transformedBase64: string = this.imageTransformService.transformImageBase64(
      this.originalImage,
      this.originalBase64,
      this.originalSize,
      this.canvasRotation,
      this.exifTransform,
      this.containWithinAspectRatio,
      this.aspectRatio
    );
    return this.setTransformedImage(transformedBase64);
  }

  private setTransformedImage(transformedBase64: string): Promise<void> {
    return new Promise<void>((resolve) => {
      this.safeImgDataUrl = this.safeResourceUrlService.toSafeUrl(transformedBase64);
      this.transformedImage = new Image();
      this.transformedImage.onload = () => {
        this.transformedSize.width = this.transformedImage.naturalWidth;
        this.transformedSize.height = this.transformedImage.naturalHeight;
        this.cd.markForCheck();
        resolve();
      };
      this.transformedImage.src = transformedBase64;
    });
  }

  public imageLoadedInView(): void {
    if (this.transformedImage != null) {
      this.imageLoaded.emit();

      this.clearImageSizeCheck();
      this.setImageMaxSizeRetries = 0;
      this.imageSizeCheckInterval = window.setInterval(() => {
        this.checkImageSize();
      }, this.RETRY_INTERVAL_MS);
    }
  }

  private clearImageSizeCheck(): void {
    if (this.imageSizeCheckInterval !== null) {
      clearInterval(this.imageSizeCheckInterval);
      this.imageSizeCheckInterval = null;
    }
  }

  private checkImageSize(): void {
    if (this.setImageMaxSizeRetries >= this.MAX_RETRY_ATTEMPTS) {
      this.clearImageSizeCheck();
      this.loadImageFailed.emit();
      return;
    }

    if (this.sourceImage?.nativeElement && this.sourceImage.nativeElement.offsetWidth > 0) {
      this.clearImageSizeCheck();
      this.setMaxSize();
      this.setCropperScaledMinSize();
      this.resetCropperPosition();
      this.cropperReady.emit({ ...this.maxSize });
      this.cd.markForCheck();
    } else {
      this.setImageMaxSizeRetries++;
    }
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.cropperPositionService.resizeCropperPosition(this.cropper, this.maxSize, this.sourceImage.nativeElement);
    this.setMaxSize();
    this.setCropperScaledMinSize();
  }

  private resetCropperPosition(): void {
    this.cropperPositionService.resetCropperPosition(
      this.sourceImage.nativeElement,
      this.cropper,
      this.maintainAspectRatio,
      this.aspectRatio,
      0, // cropperStaticWidth removed
      0 // cropperStaticHeight removed
    );
    this.crop();
    this.imageVisible = true;
  }

  public keyboardAccess(event: any): void {
    this.stepSize = this.gestureService.changeKeyboardStepSize(event, this.stepSize);
    const keyboardResult: any = this.gestureService.handleKeyboardNavigation(event, this.stepSize);
    if (keyboardResult) {
      this.startMove({ clientX: 0, clientY: 0 }, keyboardResult.moveType, keyboardResult.position);
      this.moveImg(keyboardResult.moveEvent);
      this.moveStop();
    }
  }

  public startMove(event: any, moveType: MoveTypes, position: string | null = null): void {
    if (this.moveStart && this.moveStart.active && this.moveStart.type === MoveTypes.PINCH) {
      return;
    }
    this.moveStart = this.gestureService.startMove(event, moveType, position, this.cropper);
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  public moveImg(event: any): void {
    if (this.moveStart.active) {
      if (event.stopPropagation) {
        event.stopPropagation();
      }
      if (event.preventDefault) {
        event.preventDefault();
      }
      if (this.moveStart.type === MoveTypes.MOVE) {
        this.move(event);
        this.cropperPositionService.checkCropperPosition(this.cropper, this.maxSize, true);
      } else if (this.moveStart.type === MoveTypes.RESIZE) {
        this.resize(event);
        this.cropperPositionService.checkCropperPosition(this.cropper, this.maxSize, false);
      }
      this.cd.detectChanges();
    }
  }

  private setMaxSize(): void {
    if (this.sourceImage) {
      const sourceImageElement: HTMLElement = this.sourceImage.nativeElement;
      this.maxSize.width = sourceImageElement.offsetWidth;
      this.maxSize.height = sourceImageElement.offsetHeight;
      this.marginLeft = this.safeResourceUrlService.toSafeStyle(`calc(50% - ${this.maxSize.width / 2}px)`);
    }
  }

  private setCropperScaledMinSize(): void {
    if (this.transformedImage) {
      const scaledSize: { width: number; height: number } = this.cropperPositionService.calculateScaledMinSize(
        this.cropperMinWidth,
        0, // cropperMinHeight removed
        this.transformedImage,
        this.maxSize,
        this.maintainAspectRatio,
        this.aspectRatio
      );
      this.cropperScaledMinWidth = scaledSize.width;
      this.cropperScaledMinHeight = scaledSize.height;
    } else {
      this.cropperScaledMinWidth = 20;
      this.cropperScaledMinHeight = 20;
    }
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  public moveStop(): void {
    if (this.moveStart.active) {
      this.moveStart = this.gestureService.stopMove(this.moveStart);
      this.crop();
    }
  }

  private move(event: any): void {
    const diffX: number = this.gestureService.getClientX(event) - this.moveStart.clientX;
    const diffY: number = this.gestureService.getClientY(event) - this.moveStart.clientY;
    this.cropperPositionService.moveCropper(this.cropper, this.moveStart, diffX, diffY);
  }

  private resize(event: any): void {
    const diffX: number = this.gestureService.getClientX(event) - this.moveStart.clientX;
    const diffY: number = this.gestureService.getClientY(event) - this.moveStart.clientY;
    this.cropperPositionService.resizeCropper(
      this.cropper,
      this.moveStart,
      diffX,
      diffY,
      this.cropperScaledMinWidth,
      this.cropperScaledMinHeight,
      this.maintainAspectRatio,
      this.aspectRatio,
      this.maxSize
    );
  }

  private crop(): ImageCroppedEvent | null {
    if (!this.sourceImage?.nativeElement || !this.transformedImage) {
      return null;
    }
    const output: ImageCroppedEvent | null = this.cropOperationsService.cropImage(
      this.cropper,
      this.transformedImage,
      this.transformedSize,
      this.sourceImage.nativeElement,
      this.transform,
      this.backgroundColor,
      this.format,
      92, // imageQuality default
      this.containWithinAspectRatio,
      this.originalSize,
      this.canvasRotation,
      this.exifTransform,
      this.resizeToWidth,
      0, // resizeToHeight removed
      this.maintainAspectRatio,
      this.aspectRatio,
      this.onlyScaleDown
    );
    if (output) {
      this.imageCropped.emit(output);
    }
    return output;
  }
}
